import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import { animeList } from "@/mocks/anime";

export default function AnimeDetail() {
  const { id } = useParams();
  const anime = animeList.find((a) => a.id === id);

  if (!anime) {
    return (
      <>
        <Header />
        <main className="flex min-h-[50vh] flex-col items-center justify-center bg-background-50 px-4 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-compass-3-line text-3xl"></i>
          </span>
          <p className="mt-4 font-bold text-foreground-900">作品不存在或已下架</p>
          <Link
            to="/series"
            className="mt-4 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            返回本周新番
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const epCount = Number(anime.latestEp.replace(/[^0-9]/g, "")) || 1;
  const episodes = Array.from({ length: epCount }, (_, i) => i + 1);
  const related = animeList.filter((a) => a.category === anime.category && a.id !== anime.id);

  const schema = {
    "@context": "https://schema.org",
    "@type": "TVSeries",
    name: anime.title,
    url: `https://comicnot.github.io/anime/${anime.id}`,
    inLanguage: "zh-CN",
    description: anime.desc,
    genre: anime.category,
    keywords: anime.tags.join(","),
    numberOfEpisodes: epCount,
    productionCompany: { "@type": "Organization", name: anime.studio },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: anime.score,
      ratingCount: anime.views,
    },
  };

  return (
    <>
      <Header />
      <main className="bg-background-50">
        <section className="bg-background-100 py-8 md:py-10">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <nav aria-label="面包屑" className="text-xs text-foreground-600">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li>/</li>
                <li><Link to="/series" className="hover:text-primary-600">本周新番</Link></li>
                <li>/</li>
                <li aria-current="page" className="text-foreground-500">{anime.title}</li>
              </ol>
            </nav>

            <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-start">
              <div className="w-full shrink-0 sm:w-64">
                <div className="aspect-[3/4] w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
                  <img
                    src={anime.cover}
                    alt={`${anime.title}封面海报`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-medium text-background-50 ${
                      anime.status === "本周完结" ? "bg-accent-500" : "bg-primary-500"
                    }`}
                  >
                    {anime.status}
                  </span>
                  <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                    {anime.category}
                  </span>
                  {anime.tags.map((t) => (
                    <span key={t} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600">
                      {t}
                    </span>
                  ))}
                </div>

                <h1 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">{anime.title}</h1>

                <div className="mt-4 flex flex-wrap gap-6 text-sm">
                  <div>
                    <p className="text-xs text-foreground-500">更新时间</p>
                    <p className="mt-0.5 font-bold text-foreground-900">{anime.day} {anime.updateTime}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">最新话</p>
                    <p className="mt-0.5 font-bold text-primary-600">{anime.latestEp}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">总集数</p>
                    <p className="mt-0.5 font-bold text-foreground-900">{anime.totalEp}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">观看热度</p>
                    <p className="mt-0.5 font-bold text-foreground-900">{anime.views}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">制作</p>
                    <p className="mt-0.5 font-bold text-foreground-900">{anime.studio}</p>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <span className="flex items-center gap-1 rounded-md bg-secondary-500 px-3 py-1.5 text-sm font-bold text-background-50">
                    <i className="ri-star-fill"></i>
                    {anime.score}
                  </span>
                  <span className="text-xs text-foreground-500">口碑评分</span>
                </div>

                <p className="mt-5 text-sm leading-relaxed text-foreground-700">{anime.desc}</p>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <a
                    href="#episodes"
                    className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-play-circle-line mr-1"></i>
                    立即观看最新话
                  </a>
                  <Link
                    to="/calendar"
                    className="whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-bold text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    加入追番日程
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="episodes" className="py-8 md:py-10">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground-950">剧集列表</h2>
              <span className="text-sm text-foreground-500">{anime.latestEp} · 已更新</span>
            </div>
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10">
              {episodes.map((ep) => (
                <div
                  key={ep}
                  className={`flex items-center justify-center rounded-md border py-2.5 text-sm font-medium transition-colors ${
                    ep === epCount
                      ? "border-primary-300 bg-primary-500 text-background-50"
                      : "border-background-200 bg-background-50 text-foreground-800 hover:border-primary-200 hover:bg-primary-100"
                  }`}
                >
                  第{ep}话
                </div>
              ))}
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="bg-background-100 py-8 md:py-10">
            <div className="mx-auto max-w-7xl px-4 md:px-6">
              <h2 className="mb-5 text-xl font-bold text-foreground-950">
                同类{anime.category}推荐
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {related.map((item) => (
                  <AnimeCard key={item.id} anime={item} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </>
  );
}